<template>
  <div v-if="dataLoaded" class="q-mt-sm">
    <div class="row gutter-sm q-mb-lg">

      <!-- Left column -->
      <div class="col-md-6 col-sm-12 col-xs-12 q-pa-sm">

        <!-- User block -->
        <div class="row q-mb-sm">
          <div class="full-width">
            <p class="card-title">
              {{ getString('module.profile.basicInfo.label') }}
            </p>
            <div class="card-block scroll">
              <!-- Field: first name -->
              <q-field class="q-mb-md"
                :error="valData.firstname.set && valData.firstname.warning"
                :error-label="valData.firstname.set ? valData.firstname.message : null">
                <q-input type="text" v-model="data.name.firstname"
                  @input="validateField('data.name.firstname', 'firstname')"
                  @blur="valData.firstname.set = true"
                  :float-label="getString('firstname')">
                </q-input>
              </q-field>

              <!-- Field: last name -->
              <q-field class="q-mb-md"
                :error="valData.surname.set && valData.surname.warning"
                :error-label="valData.surname.set ? valData.surname.message : null">
                <q-input type="text" v-model="data.name.surname"
                  @input="validateField('data.name.surname', 'surname')"
                  @blur="valData.surname.set = true"
                  :float-label="getString('surname')">
                </q-input>
              </q-field>

              <!-- Field: email -->
              <q-field class="q-mb-md"
                :error="valData.email.set && valData.email.warning"
                :error-label="valData.email.set ? valData.email.message : null">
                <q-input type="text" v-model="data.email"
                  @input="validateField('data.email', 'email')"
                  @blur="valData.email.set = true"
                  :float-label="getString('email')">
                </q-input>
              </q-field>
            </div>
          </div>
        </div>

        <!-- Save section changes -->
        <div class="row q-mb-lg section-save">
          <q-btn
            class="capitalize ctrl-button right"
            :disable="!containsChanges('basicInfo')
              || containsErrors('basicInfo')"
            :color="containsChanges('basicInfo')
              && !containsErrors('basicInfo') ? 'primary' : 'grey-5'"
            :label="getString('action.save')"
            @click="saveChanges('basicInfo')"
          />
        </div>

        <!-- Settings block -->
        <div class="row q-mb-sm">
          <div class="full-width">
            <p class="card-title">{{ getString('settings_str') }}</p>
            <div class="card-block scroll">
              <div class="list item-inset-delimiter">
                <!-- Language select -->
                <q-field class="q-mb-md">
                  <q-select
                    v-model="data.settings.language"
                    :options="languages"
                    :float-label="getString('language')"
                  />
                </q-field>

                <!-- Date format select -->
                <q-field class="q-mb-md">
                  <q-select
                    v-model="data.settings.date"
                    :options="dateFormat"
                    :float-label="$store.state.stringsStore.strings
                    .module.profile.settings.date.label"
                  />
                </q-field>

                <!-- Time format select -->
                <q-field class="q-mb-md">
                  <q-select
                    v-model="data.settings.time"
                    :options="timeFormat"
                    :float-label="$store.state.stringsStore.strings
                    .module.profile.settings.time.label"
                  />
                </q-field>
              </div>
            </div>
          </div>
        </div>

        <div class="row q-mb-lg section-save">
          <q-btn
            class="capitalize ctrl-button right"
            :disable="!containsChanges('settings')"
            :color="containsChanges('settings') ? 'primary' : 'grey-5'"
            :label="`${getString('action.save')}`"
            @click="saveChanges('settings')"
          />
        </div>

      </div>

      <!-- Right column -->
      <div class="col-md-6 col-sm-12 col-xs-12">

        <!-- Avatar upload block -->
        <div class="row q-mb-lg">
          <div class="full-width">
            <p class="card-title">
              {{ getString('module.profile.avatar.label') }}
            </p>
            <div class="card-block scroll">
              <div id="drop-avatar">
                <div class="dropzone-custom-content">
                  <div class="row">
                    <div class="q-pa-md">
                      <img :src="`${env_url}${$store.state.meStore.me.avatar}`" class="gravatar" alt="">
                    </div>
                    <div class="col">
                      <div class="q-pa-md">
                        <p class="dropzone-custom-title">
                          {{ fetchVariableString('upload.hint',
                            '%file%', 'module.profile.avatar.a') }}
                        </p>
                        <q-uploader :url="`${env_url}me/avatar/`"
                          method="PUT"
                          :with-credentials="true"
                          :extensions="'.jpg, .jpeg, .png'"
                          @add="addFile"
                          @uploaded="uploadSuccess()"
                          @fail="uploadFail()"
                          :headers="{'Content-Type': `${file.type}`,
                          'Content-Disposition': `attachment; filename=&quot;${file.name}&quot;`}"
                          :send-raw="true"/>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Password block -->
        <div class="row q-mb-sm">
          <div class="full-width">
            <p class="card-title">
              {{ getString('password_str') }}
            </p>
            <div class="card-block scroll">
              <!-- Field: Old password -->
              <q-field class="q-mb-md"
                :error="valData.oldPassword.set && valData.oldPassword.warning"
                :error-label="valData.oldPassword.set ? valData.oldPassword.message : null">
                <q-input type="password" v-model="password.old"
                  @blur="valData.oldPassword.set = true"
                  @input="validateField('password.old', 'oldPassword')"
                  :float-label="getString('password.old')">
                </q-input>
              </q-field>

              <!-- Field: New password -->
              <q-field class="q-mb-md"
                :error="valData.newPassword.set && valData.newPassword.warning"
                :error-label="valData.newPassword.set ? valData.newPassword.message : null">
                <q-input type="password" v-model="password.new"
                  @blur="valData.newPassword.set = true"
                  @input="validateField('password.new', 'newPassword')"
                  :float-label="getString('password.new')">
                </q-input>
              </q-field>

              <!-- Field: Retype password -->
              <q-field class="q-mb-md"
                :error="valData.retypePassword.set && valData.retypePassword.warning"
                :error-label="valData.retypePassword.set ? valData.retypePassword.message : null">
                <q-input type="password" v-model="password.retype"
                  @blur="valData.retypePassword.set = true"
                  @input="validateField('password.retype', 'retypePassword')"
                  :float-label="getString('password.retype')">
                </q-input>
              </q-field>
              <div class="col-12 q-pa-md message">
                <p class="text-weight-bold">
                  {{ getString('password.requirements.head') }}
                </p>
                <ul>
                  <li>
                    {{ getString('password.requirements.characters') }}
                  </li>
                  <li>
                    {{ getString('password.requirements.digits') }}
                  </li>
                  <li>
                    {{ getString('password.requirements.capitals') }}
                  </li>
                  <li>
                    {{ getString('password.requirements.specialchars') }}
                  </li>
                  <li>! ? $ % ^ &amp; * ( ) _ \ - + = { } [ ] : ; @ ' ~ # | &lt; , &gt; . /</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <!-- Save section changes -->
        <div class="row q-mb-lg section-save">
          <q-btn
            class="capitalize ctrl-button right"
            :disable="!containsChanges('password')
              || containsErrors('password')"
            :color="containsChanges('password')
              && !containsErrors('password') ? 'primary' : 'grey-5'"
            :label="getString('action.save')"
            @click="saveChanges('password')"
          />
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import _get from 'lodash.get';
import _set from 'lodash.set';
import _cloneDeep from 'lodash.clonedeep';
import getString from '@/helpers/GetStrings';

import moment from 'moment';
import { helpers, required, email, minLength, sameAs } from 'vuelidate/lib/validators';

// Define custom validators
const alpha = helpers.regex('alpha', /^[a-zA-Z- ]*$/);
const pwRequirements = helpers.regex('pwRequirements', /^(?=(.*[A-Z]){2})(?=(.*[^\w\s]){2})(?=(.*[0-9]){2})(?=(^.{10,}$))/);

export default {
  components: {
  },
  name: 'MeProfile',
  validations: {
    data: {
      email: {
        required,
        email,
      },
      name: {
        firstname: {
          alpha,
          required,
        },
        surname: {
          alpha,
          required,
        },
      },
    },
    password: {
      old: {
        required,
      },
      new: {
        required,
        pwRequirements,
        minLength: minLength(10),
      },
      retype: {
        required,
        sameAsPassword: sameAs('new'),
      },
    },
  },
  data() {
    return {
      env_url: `${process.env.VUE_APP_API_URL}`,
      languages: [
        {
          value: 'en',
          label: 'English',
        },
        {
          value: 'nl',
          label: 'Nederlands',
        },
      ],
      dateFormat: [
        {
          value: 'yyyy-MM-dd',
          label: moment().format('YYYY-MM-DD'),
        },
        {
          value: 'dd-MM-yyyy',
          label: moment().format('DD-MM-YYYY'),
        },
        {
          value: 'd-M-yyyy',
          label: moment().format(`D-M-YYYY [(
            ${getString('module.profile.settings.leading')}
            )]`),
        },
        {
          value: 'dd-MM-yy',
          label: moment().format('DD-MM-YY'),
        },
        {
          value: 'd-M-yy',
          label: moment().format(`D-M-YY [(
            ${getString('module.profile.settings.leading')}
            )]`),
        },
      ],
      timeFormat: [
        {
          value: 'HH:mm',
          label: `${moment('20180208T2005').format('HH:mm')}
          / ${moment('20180208T0805').format('HH:mm')}`,
        },
        {
          value: 'H:mm',
          label: `${moment('20180208T2005').format('H:mm')}
          / ${moment('20180208T0805').format('H:mm')} 
          (${getString('module.profile.settings.leading')})`,
        },
        {
          value: 'hh:mm a',
          label: `${moment('20180208T2005').format('hh:mm a')}
          / ${moment('20180208T0805').format('hh:mm a')}`,
        },
        {
          value: 'h:mm a',
          label: `${moment('20180208T2005').format('h:mm a')}
          / ${moment('20180208T0805').format('h:mm a')} 
          (${getString('module.profile.settings.leading')})`,
        },
      ],
      valData: {
        oldPassword: {
          set: false,
          warning: false,
          message: '',
        },
        newPassword: {
          set: false,
          warning: false,
          message: '',
        },
        retypePassword: {
          set: false,
          warning: false,
          message: '',
        },
        firstname: {
          set: false,
          warning: false,
          message: '',
        },
        surname: {
          set: false,
          warning: false,
          message: '',
        },
        email: {
          set: false,
          warning: false,
          message: '',
        },
      },
      dataLoaded: false,
      data: null,
      password: {
        old: '',
        new: '',
        retype: '',
      },
      isLoading: false,
      file: {
        name: '',
        type: '',
      },
    };
  },
  methods: {
    getString,
    async uploadSuccess() {
      try {
        await this.$store.dispatch('getMe');
        this.$root.$emit('me:avatar-update');
        // Notify user that the upload has succeeded.
        this.$q.notify({
          message: getString('module.profile.avatar.save.success'),
          type: 'positive',
          timeout: 1200,
          position: 'top-right',
        });
      } catch (error) {
        // Handle promise rejection.
        console.warn(error);
      }
    },
    uploadFail() {
      // Notify user that the upload has failed.
      this.$q.notify({
        message: getString('error.general'),
        type: 'negative',
        timeout: 2000,
        position: 'top-right',
      });
    },
    addFile(file) {
      this.file.name = file[0].name;
      this.file.type = file[0].type;
    },
    handleFileUpload() {
      this.file = this.$refs.file.files[0];
    },
    submitFile() {
      const formData = new FormData();
      formData.append('file', this.file);
    },
    validateField(value, field) {
      if ((field === 'firstname' || field === 'surname')
        && !_get(this.$v, `${value}.alpha`)) {
        _set(this.valData, `${field}.warning`, true);
        _set(this.valData, `${field}.message`,
          this.getValidationString(field, 'form.error.pattern.default'));
      } else if (field === 'email' && !_get(this.$v, `${value}.email`)) {
        _set(this.valData, `${field}.warning`, true);
        _set(this.valData, `${field}.message`,
          this.getValidationString(field, 'form.error.pattern.email'));
      } else if (field === 'newPassword' && !_get(this.$v, `${value}.pwRequirements`)) {
        _set(this.valData, `${field}.warning`, true);
        _set(this.valData, `${field}.message`,
          this.getValidationString('password', 'form.error.pattern.password'));
      } else if (field === 'retypePassword' && !_get(this.$v, `${value}.sameAsPassword`)) {
        _set(this.valData, `${field}.warning`, true);
        _set(this.valData, `${field}.message`,
          this.getValidationString('password', 'password.retype_error'));
      } else if (!_get(this.$v, `${value}.required`)) {
        _set(this.valData, `${field}.warning`, true);
        _set(this.valData, `${field}.message`,
          this.getValidationString(field, 'form.error.required'));
      } else {
        this.valData[field].warning = false;
        this.valData[field].message = '';
      }
      return this.valData[field].warning;
    },
    containsErrors(section) {
      let errors = false;
      switch (section) {
        case 'basicInfo':
          this.$v.data.$touch();
          errors = this.$v.data.$error;
          break;
        case 'password':
          this.$v.password.$touch();
          errors = this.$v.password.$error;
          break;

        default:
          break;
      }
      return errors;
    },
    containsChanges(section) {
      // Compares store state to inputs and returns true if differences exists.
      let changes = false;
      switch (section) {
        case 'basicInfo':
          changes = this.$store.state.meStore.me.name.firstname !== this.data.name.firstname
            || this.$store.state.meStore.me.name.surname !== this.data.name.surname
            || this.$store.state.meStore.me.email !== this.data.email;
          break;
        case 'settings':
          changes = this.$store.state.meStore.me.settings.date !== this.data.settings.date
              || this.$store.state.meStore.me.settings.time !== this.data.settings.time
              || this.$store.state.meStore.me.settings.language !== this.data.settings.language;
          break;
        case 'password':
          changes = this.password.new !== ''
            && this.password.old !== ''
            && this.password.retype !== '';
          break;
        default:
          break;
      }
      return changes;
    },
    getValidationString(field, error) {
      // Field refers to the input field where the function is called.
      // Error is the path to the error string within strings.form.error.
      let message = _get(this.$store.state.stringsStore.strings, error);

      // If the string contains %possesivePronoun%, replace with string.
      if (message.match('%possesivePronoun%')) {
        message = message.replace('%possesivePronoun%',
          `${getString('possesivePronoun.your')} `);
      }

      // If the string contains %input%
      if (message.match('%input%')) {
        // Adjust old and new password fields in order to fetch strings.
        if (field === 'newPassword') {
          field = 'password.new';
        } else if (field === 'oldPassword') {
          field = 'password.old';
        }

        // Fetch replacement string based on field.
        const replacement = _get(this.$store.state.stringsStore.strings, field).toLowerCase();

        // Return message string
        message = message.replace('%input%', replacement);
      }

      // Return the message string.
      return message;
    },
    fetchVariableString(path, variable, replacementPath) {
      // Get the string containing the variable.
      let message = _get(this.$store.state.stringsStore.strings, path);

      // Get the replacement string.
      const replacement = _get(this.$store.state.stringsStore.strings, replacementPath);

      // Replace the variable in the message string.
      if (message.match(variable)) {
        message = message.replace(variable, replacement);
      }

      // Return the joined string.
      return message;
    },
    gotoRoute(path) {
      this.$router.push(path);
    },
    getPutParams(section) {
      // This function returns a param string for a given SECTION.
      let params = [];
      switch (section) {
        case 'basicInfo':
          params = [
            `firstname=${encodeURIComponent(this.data.name.firstname)}`,
            `surname=${encodeURIComponent(this.data.name.surname)}`,
            `email=${encodeURIComponent(this.data.email)}`,
          ].filter(val => val !== null).join('&');
          break;
        case 'settings':
          params = [
            `language=${encodeURIComponent(this.data.settings.language)}`,
            `date=${encodeURIComponent(this.data.settings.date)}`,
            `time=${encodeURIComponent(this.data.settings.time)}`,
          ].filter(val => val !== null).join('&');
          break;
        case 'password':
          params = [
            `old_password=${encodeURIComponent(this.password.old)}`,
            `new_password=${encodeURIComponent(this.password.new)}`,
          ].filter(val => val !== null).join('&');
          break;
        default:
          break;
      }
      return params;
    },
    async saveChanges(section) {
      // Since we save each section seperately, we send a PUT request to the relevant endpoint.
      // The basic section is sent to /api/me/, and thus endpoint is an empty string.
      const endpoint = section === 'basicInfo' ? '' : section;

      try {
        // Post the updated parameters to the relevant endpoint.
        await this.axios.put(`/me/${endpoint}`, this.getPutParams(section));
        // On successful PUT, get updated 'me'.
        await this.$store.dispatch('getMe');

        // Clear password inputs on successfull password change.
        if (endpoint === 'password') {
          this.password.old = '';
          this.password.new = '';
          this.password.retype = '';
        }

        // Notify user that the change has succeeded.
        this.$q.notify({
          message: getString(`module.profile[${section}].save.success`),
          type: 'positive',
          timeout: 1200,
          position: 'top-right',
        });
      } catch (error) {
        // Set general error message.
        let message = getString('error.general');

        // Check if old password was entered correctly and if not, set new error message.
        if (endpoint === 'password' && error.response.data.code === 'OLD_PASSWORD_INCORRECT') {
          message = getString('module.profile.password.save.error.old');

          // Display the error message on the new password input field.
          this.valData.oldPassword.set = true;
          this.valData.oldPassword.warning = true;
          this.valData.oldPassword.message = message;
        }

        // Notify the user about the error.
        this.$q.notify({
          message,
          type: 'negative',
          timeout: 2000,
          position: 'top-right',
        });
        console.warn(error);
      }
    },
  },
  mounted() {
    // Dispatch a new getMe action to ensure up to date data.
    this.$store.dispatch('getMe')
      .then(() => {
        // Clone the 'me' object (prevents binding to the store state directly)
        this.data = _cloneDeep(this.$store.state.meStore.me);
        this.dataLoaded = true;
      })
      .catch((error) => {
        console.warn(error);
      });
  },
};
</script>

<style lang="stylus" scoped>
@import '~variables';
.full-width
  width 100%

.list
  .delimiter:not(:last-child)
    border-bottom 1px solid #f2f2f2

p.validation-warning
  font-size 85%
  margin-bottom 0
  margin-top 10px
  color $ctrl-red

.gravatar
  display inline-block
  height 104px
  width 104px
  object-fit cover
  border-radius 50%

.section-save
  justify-content flex-end

.message
  border 1px solid rgba(0, 0, 0, 0.08)
  border-radius 2px
  padding 1rem
  line-height 1.2rem
  color rgba(0, 0, 0, 0.5)
  text-shadow rgba(255, 255, 255, 0.4) 0 1px 0
  background-color rgba(0, 0, 0, 0.015)

#drop-avatar
  cursor pointer
</style>
